import React, { useMemo, useState } from 'react';
import { Clock, GripVertical } from 'lucide-react';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// How many visit cards to show inline before collapsing the rest behind "+N more".
const VISIBLE = 3;

// The strip of visits that have no carer for the selected day. A left summary
// card explains the drag gesture; each visit card states WHY it's unassigned,
// can be dragged onto a carer row, and offers a one-click Assign fallback.
const UnassignedVisits = ({ visits = [], onAssign, zones = [], date, onDragVisit, weekCount = 0 }) => {
  const [expanded, setExpanded] = useState(false);

  // Zone id → { name, color } for the zone badge.
  const zoneMap = useMemo(() => {
    const m = new Map();
    zones.forEach((z) => m.set(String(z.id), { name: z.name, color: z.color }));
    return m;
  }, [zones]);

  // Every unassigned visit for the selected day needs a carer — show them all
  // (an uncovered visit stays actionable even after its scheduled time).
  const shown = visits;

  if (shown.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/10 dark:text-emerald-400">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">✓</span>
        Every visit has a carer for this day.
        {weekCount > 0 && (
          <span className="ml-1 text-[12px] font-semibold text-purple-500">· {weekCount} unassigned this week</span>
        )}
      </div>
    );
  }

  // Date label for the summary card, e.g. "THU 18 Jun".
  const dateLabel = (() => {
    if (!date) return '';
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return `${DOW[dt.getDay()].toUpperCase()} ${d} ${MON[m - 1]}`;
  })();

  const visible = expanded ? shown : shown.slice(0, VISIBLE);
  const hiddenCount = shown.length - visible.length;

  return (
    <div className="flex gap-2 overflow-x-auto rounded-xl border border-purple-100 bg-purple-50/60 px-2 py-1.5 dark:border-purple-900/40 dark:bg-purple-950/20">
      {/* Summary / instructions card */}
      <div className="flex min-w-[190px] max-w-[190px] flex-col justify-center border-r border-purple-200 px-2 pr-3 dark:border-purple-900/40">
        <div className="text-[12px] font-bold text-purple-700 dark:text-purple-300">Unassigned · {dateLabel}</div>
        <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
          {shown.length} today
          <span className="text-gray-400 dark:text-gray-500"> · {weekCount} this week</span>
        </div>
        <div className="text-[9px] leading-snug text-gray-400 dark:text-gray-500">
          Drag a card onto a green carer row, or use Assign.
        </div>
      </div>

      {/* Visit cards */}
      {visible.map((v) => {
        const zone = v.zone_id != null ? zoneMap.get(String(v.zone_id)) : null;
        return (
          <div
            key={`${v.assignment_id}`}
            draggable
            onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragVisit && onDragVisit(v); }}
            onDragEnd={() => onDragVisit && onDragVisit(null)}
            className="flex min-w-[250px] cursor-grab items-center gap-2 rounded-lg border border-purple-200 bg-white dark:border-purple-900/50 dark:bg-[#31373f] px-2.5 py-1.5 shadow-sm active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4 flex-shrink-0 text-purple-300 dark:text-purple-600" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-bold text-gray-800 dark:text-gray-200">
                {v.resident?.name}{v.package_name ? <span className="font-semibold text-gray-500 dark:text-gray-400"> · {v.package_name}</span> : null}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                  <Clock className="mr-0.5 h-2.5 w-2.5" />{v.start}{v.end ? `–${v.end}` : ''}
                </span>
                {zone && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-[9px] font-bold text-gray-600 dark:bg-[#3a4048] dark:text-gray-300">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: zone.color || '#94a3b8' }} />
                    {zone.name}
                  </span>
                )}
              </div>
              {v.reason && <div className="mt-0.5 truncate text-[10px] font-medium text-purple-500 dark:text-purple-400">{v.reason}</div>}
            </div>
            <button
              onClick={() => onAssign(v)}
              className="h-7 flex-shrink-0 rounded-md border border-purple-300 px-3 text-[11px] font-bold text-purple-600 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-900/20"
            >
              Assign
            </button>
          </div>
        );
      })}

      {/* Overflow */}
      {hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="flex min-w-[80px] flex-col items-center justify-center rounded-xl border border-dashed border-purple-300 px-3 text-[12px] font-bold text-purple-500 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-900/20"
        >
          + {hiddenCount} more
        </button>
      )}
    </div>
  );
};

export default UnassignedVisits;
